import { TLocale } from '@aviv-estimation-funnel/shells/src/app/helpers/i18n';
import { AutocompleteData, GeoPlace, ParentTypes } from '@mfe-types/geo';

export const computeCitiesValues = (fetchedCities: Array<AutocompleteData>) => {
    return fetchedCities.map(({ id, name, postalCode }: AutocompleteData) => ({
        id,
        label: name,
        value: name,
        postalCode,
    }));
};

export const filterCityResults = (autocompleteResponse: GeoPlace[], text: string) => {
    // filter geoApi response to get only cities that have the given postal code in their postal codes list
    return autocompleteResponse.filter((item) => item.postal_codes?.includes(text));
};

export const extractAutocompleteData = (autocompleteResponse: GeoPlace[], locale: TLocale) => {
    return autocompleteResponse.map((item) => {
        const { name, display_name: displayName } = item.names[locale]
            ? item.names[locale][0]
            : item.names[item.language][0];

        return {
            id: item.id,
            name,
            displayName,
            postalCode: item.main_postal_code,
        };
    });
};

export const getParentByType = (placeApiResponse: GeoPlace, parentType: ParentTypes) => {
    // Checking also the postal code to avoid getting the wrong parent
    // Eg. Api call with zip 75011 returns several arrondissements and not only Paris 11ème
    let parent = placeApiResponse?.parents?.filter(
        (parent) =>
            parent.type === parentType &&
            parent.main_postal_code === placeApiResponse.main_postal_code,
    );

    if (!parent?.length) {
        parent = placeApiResponse?.parents?.filter(
            (parent) =>
                parent.type === parentType &&
                parent.postal_codes?.includes(placeApiResponse.main_postal_code),
        );
    }

    return parent?.[0];
};

const extractCityParent = (item: GeoPlace, isGermanPortal: boolean, locale: TLocale) => {
    let cityParent = getParentByType(item, ParentTypes.Borough);
    // TODO https://avivgroup.atlassian.net/browse/VEGA-1242
    // remove filter when geoApi is ready for DE portals
    if (!cityParent || isGermanPortal) {
        cityParent = getParentByType(item, ParentTypes.Municipality);
    }

    if (!cityParent) return;
    return {
        postalCode: cityParent.main_postal_code,
        cityName:
            cityParent.names?.[locale]?.[0].name ?? cityParent.names[cityParent.language][0].name,
        cityId: cityParent.id,
    };
};

export const extractDataAtHouseNumberLevel = (
    item: GeoPlace,
    isGermanPortal: boolean,
    locale: TLocale,
) => {
    const cityParent = extractCityParent(item, isGermanPortal, locale);
    const streetParent = getParentByType(item, ParentTypes.Street);
    return {
        postalCode: item?.main_postal_code ?? cityParent?.postalCode,
        cityName: cityParent?.cityName,
        cityId: cityParent?.cityId,
        streetName:
            streetParent?.names?.[locale]?.[0].name ??
            streetParent?.names?.[streetParent.language][0].name,
        streetId: streetParent?.id,
        houseNumber: item?.names?.[locale]?.[0].name ?? item?.names[item.language][0].name,
        addressId: item?.id,
    };
};

export const extractDataAtStreetLevel = (
    item: GeoPlace,
    isGermanPortal: boolean,
    locale: TLocale,
) => {
    const cityParent = extractCityParent(item, isGermanPortal, locale);
    let streetName, streetId;
    if (cityParent) {
        streetName = item?.names?.[locale]?.[0].name ?? item?.names[item.language][0].name;
        streetId = item?.id;
    }
    return {
        postalCode: item?.main_postal_code ?? cityParent?.postalCode,
        cityName: cityParent?.cityName,
        cityId: cityParent?.cityId,
        streetName,
        streetId,
    };
};

export const extractDataAtCityLevel = (
    items: Array<GeoPlace>,
    locale: TLocale,
    cityParam?: string | null,
) => {
    // Define the first item by default
    let item = items[0];

    // If there are several city results, pick the city that corresponds to the one the user is looking for.
    if (cityParam && items.length > 1) {
        const itemWithCorrespondingCity = items.find((item) => {
            const cityItemName =
                item?.names?.[locale]?.[0].name ?? item?.names[item.language][0].name;
            return cityItemName?.toLowerCase() === cityParam?.toLowerCase();
        });
        item = itemWithCorrespondingCity ?? item;
    }

    return {
        postalCode: item?.main_postal_code,
        cityName: item?.names?.[locale]?.[0].name ?? item?.names[item.language][0].name,
        cityId: item?.id,
    };
};

export const extractDataAtPostalCodeLevel = (item: GeoPlace) => {
    return {
        postalCode: item?.main_postal_code,
    };
};
